import React, { FC, useEffect, useMemo, useState } from 'react';
import './onboarding-overview.scss';

/**
 * Top of the Onboarding tab: 7-day retention hero, headline numbers, daily
 * sign-ups and the left-to-right onboarding funnel through retention.
 * Response shapes mirror GET /activation-stats/onboarding-overview and
 * /link-clicks in dream-be.
 */

interface RetentionWindow {
  retained: number;
  eligible: number;
  rate: number | null;
}

interface OnboardingOverviewResponse {
  success: boolean;
  newUsers: number;
  completed: number;
  activationRate: number | null;
  retention7d: RetentionWindow;
  dailySignups: Array<{ day: string; signups: number; rollingAvg: number }>;
  weeklyAvgSignups: number;
  recentSignups: { last7Days: number; avgPerDayLast30: number };
  funnel: {
    sessions: number;
    firstMessage: number;
    openedSetup: number;
    sections: Array<{ key: string; label: string; count: number }>;
    sectionsApprox: boolean;
    completed: number;
    completedWithoutSetup: number;
    continuedMessaging: number;
    retention: {
      h24: RetentionWindow;
      h72: RetentionWindow;
      d7: RetentionWindow;
    };
  };
}

interface LinkClicksResponse {
  success: boolean;
  clicks: number;
  taps: number;
  trackedSince: string | null;
}

interface FunnelColumn {
  key: string;
  label: string;
  count: number | null;
  /** Shown under the count; defaults to share of first messages. */
  detail: string;
  kind: 'entry' | 'section' | 'core' | 'retention';
}

interface Props {
  authGet: <T>(path: string) => Promise<T>;
  cohortParams: string;
  invitedFrom: string;
  variant: string;
  cohortSummary: string;
}

const pct = (rate: number | null) =>
  rate == null ? '—' : `${Math.round(rate * 100)}%`;

/** 7-day retention to one decimal, as on the Scaling tab. */
const pct1 = (rate: number | null) =>
  rate == null ? '—' : `${(rate * 100).toFixed(1)}%`;

const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });

const SignupChart: FC<{ days: OnboardingOverviewResponse['dailySignups'] }> = ({
  days,
}) => {
  if (days.length === 0) return null;
  const max = days.reduce((m, d) => Math.max(m, d.signups), 1);
  const w = 100 / days.length;
  const y = (n: number) => 100 - (n / max) * 100;
  const line = days
    .map((d, i) => `${i * w + w / 2},${y(d.rollingAvg)}`)
    .join(' ');
  return (
    <div className='signup-chart'>
      <svg viewBox='0 0 100 100' preserveAspectRatio='none' role='img'>
        {days.map((d, i) => (
          <rect
            key={d.day}
            x={i * w + w * 0.15}
            width={w * 0.7}
            y={y(d.signups)}
            height={100 - y(d.signups)}
          >
            <title>
              {fmtDay(d.day)}: {d.signups} sign-up{d.signups === 1 ? '' : 's'}
            </title>
          </rect>
        ))}
        <polyline points={line} vectorEffect='non-scaling-stroke' />
      </svg>
      <div className='signup-chart-axis'>
        <span>{fmtDay(days[0].day)}</span>
        <span>{fmtDay(days[days.length - 1].day)}</span>
      </div>
    </div>
  );
};

const OnboardingOverviewPanel: FC<Props> = ({
  authGet,
  cohortParams,
  invitedFrom,
  variant,
  cohortSummary,
}) => {
  const variantSelected = variant !== '';
  const noSetupPage = variant === 'chat';
  const [overview, setOverview] = useState<OnboardingOverviewResponse | null>(
    null,
  );
  const [clicks, setClicks] = useState<LinkClicksResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setOverview(null);
    setError(null);
    authGet<OnboardingOverviewResponse>(
      `/activation-stats/onboarding-overview?${cohortParams}`,
    )
      .then((data) => !cancelled && setOverview(data))
      .catch(() => !cancelled && setError('Failed to load onboarding funnel'));
    return () => {
      cancelled = true;
    };
  }, [authGet, cohortParams]);

  useEffect(() => {
    let cancelled = false;
    setClicks(null);
    authGet<LinkClicksResponse>(`/activation-stats/link-clicks?${cohortParams}`)
      .then((data) => !cancelled && setClicks(data))
      .catch(() => !cancelled && setClicks(null));
    return () => {
      cancelled = true;
    };
  }, [authGet, cohortParams]);

  const columns: FunnelColumn[] = useMemo(() => {
    if (!overview) return [];
    const f = overview.funnel;
    const ofFirst = (n: number) =>
      f.firstMessage > 0 ? `${pct(n / f.firstMessage)} of first messages` : '';
    const clickTracked = clicks?.trackedSince != null;
    const retention = (
      key: string,
      label: string,
      w: RetentionWindow,
    ): FunnelColumn => ({
      key,
      label,
      count: w.retained,
      detail: `${(key === 'd7' ? pct1 : pct)(w.rate)} of ${w.eligible} eligible`,
      kind: 'retention',
    });
    return [
      {
        key: 'clicked',
        label: 'Viewed invite page',
        count: variantSelected || !clickTracked ? null : clicks!.clicks,
        detail: variantSelected
          ? 'Not split by variant'
          : !clickTracked
            ? 'Tracking starts at launch'
            : clicks!.trackedSince! > invitedFrom
              ? `Since ${fmtDay(clicks!.trackedSince!)}`
              : 'Dream-hosted links',
        kind: 'entry',
      },
      {
        key: 'first-message',
        label: 'Sent first message',
        count: f.firstMessage,
        detail: `${f.sessions} conversations started`,
        kind: 'core',
      },
      {
        key: 'opened',
        label: 'Opened setup',
        count: noSetupPage ? null : f.openedSetup,
        detail: noSetupPage
          ? 'No setup page in this flow'
          : ofFirst(f.openedSetup),
        kind: 'core',
      },
      ...f.sections.map(
        (s): FunnelColumn => ({
          key: `section-${s.key}`,
          label: s.label,
          count: s.count,
          detail: ofFirst(s.count),
          kind: 'section',
        }),
      ),
      {
        key: 'completed',
        label: 'Completed',
        count: f.completed,
        detail: ofFirst(f.completed),
        kind: 'core',
      },
      {
        key: 'continued',
        label: 'Continued messaging',
        count: f.continuedMessaging,
        detail:
          f.completed > 0
            ? `${pct(f.continuedMessaging / f.completed)} of completers`
            : '',
        kind: 'core',
      },
      retention('h24', '24h retention', f.retention.h24),
      retention('h72', '72h retention', f.retention.h72),
      retention('d7', '7-day retention', f.retention.d7),
    ];
  }, [overview, clicks, variantSelected, noSetupPage, invitedFrom]);

  if (error) return <div className='msg error'>{error}</div>;
  if (!overview) {
    return (
      <section className='activation-section'>
        <div className='loading'>Loading...</div>
      </section>
    );
  }

  const r7 = overview.retention7d;
  const latest = overview.dailySignups[overview.dailySignups.length - 1];
  const barMax = Math.max(
    1,
    ...columns.map((c) => (c.kind === 'entry' ? 0 : (c.count ?? 0))),
    columns[0]?.count ?? 0,
  );
  const sectionCount = overview.funnel.sections.length;

  return (
    <>
      <section className='activation-section onboarding-hero'>
        <div className='hero-retention'>
          <span className='hero-cohort'>{cohortSummary}</span>
          <span className='hero-label'>7-day retention</span>
          <strong className='hero-value'>{pct1(r7.rate)}</strong>
          <div className='hero-bar'>
            <div style={{ width: `${Math.round((r7.rate ?? 0) * 100)}%` }} />
          </div>
          <span className='hero-sub'>
            {r7.eligible > 0
              ? `${r7.retained} of ${r7.eligible} people who signed up in this range and finished onboarding messaged on day 7 (the 24h starting 7 days after sign-up). Same people and filters as the Scaling tab, minus Dream staff and test users.`
              : 'Too early to tell: nobody who finished onboarding signed up 8+ days ago in this view'}
          </span>
        </div>
        <div className='hero-stats'>
          <div className='hero-stat'>
            <span className='hero-label'>New users</span>
            <div className='hero-split'>
              <div>
                <strong>{overview.recentSignups.last7Days}</strong>
                <small>in the last 7 days</small>
              </div>
              <div>
                <strong>
                  {overview.recentSignups.avgPerDayLast30.toFixed(1)}
                </strong>
                <small>a day on average, last 30 days</small>
              </div>
            </div>
          </div>
          <div className='hero-stat'>
            <span className='hero-label'>Activation rate</span>
            <strong>{pct(overview.activationRate)}</strong>
            <small>
              {overview.completed} of {overview.newUsers} finished onboarding
            </small>
          </div>
          <div className='hero-stat hero-stat--wide'>
            <div className='hero-stat-head'>
              <div>
                <span className='hero-label'>Daily new sign-ups</span>
                <strong>{latest?.signups ?? 0}</strong>
                <small>
                  {latest ? fmtDay(latest.day) : ''} · weekly average{' '}
                  {overview.weeklyAvgSignups.toFixed(1)}/day
                </small>
              </div>
              <span className='signup-legend'>
                <i className='legend-bar' /> per day
                <i className='legend-line' /> 7-day average
              </span>
            </div>
            <SignupChart days={overview.dailySignups} />
          </div>
        </div>
      </section>

      <section className='activation-section'>
        <h3>
          Onboarding funnel
          <span
            className='info-dot'
            title='Everyone whose onboarding conversation started in the date range. Retention at 24h / 72h / 7 days = sent a message at least that long after finishing onboarding, out of people who finished at least that long ago.'
          >
            ⓘ
          </span>
        </h3>
        <p className='activation-note'>
          Who reached each step, left to right.
          {!variantSelected &&
            ' Pick a variant to see each section of the setup page.'}
          {overview.funnel.sectionsApprox &&
            ' Section counts for people who started before first-reach tracking are estimated from the furthest step they reached.'}
          {!noSetupPage &&
            overview.funnel.completedWithoutSetup > 0 &&
            ` ${overview.funnel.completedWithoutSetup} of the people who completed never opened the setup page (they finished in chat), so Completed can be higher than Opened setup.`}
        </p>
        <div className='onboarding-funnel-flow'>
          {columns.map((c, i) => {
            const isFirstSection =
              c.kind === 'section' && columns[i - 1]?.kind !== 'section';
            return (
              <div
                key={c.key}
                className={`flow-col flow-col--${c.kind}${
                  c.count == null ? ' flow-col--empty' : ''
                }`}
              >
                {isFirstSection && (
                  <span
                    className='flow-group-label'
                    style={{
                      width: `calc(${sectionCount * 100}% + ${
                        (sectionCount - 1) * 8
                      }px)`,
                    }}
                  >
                    Setup sections
                  </span>
                )}
                <div className='flow-count'>{c.count ?? '—'}</div>
                <div className='flow-track'>
                  <div
                    className='flow-bar'
                    style={{
                      height: `${c.count == null ? 0 : (c.count / barMax) * 100}%`,
                    }}
                  />
                </div>
                <div className='flow-label'>{c.label}</div>
                <div className='flow-detail'>{c.detail}</div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default OnboardingOverviewPanel;
